import React, { useCallback, useEffect, useState } from 'react';
import { BookOpenText, RefreshCw, ShieldCheck } from 'lucide-react';
import api from '../services/api';
import './Assistant.css';
import './FaqLuecken.css';
import PageHeader from '../components/PageHeader';

// SUPPORT-FAQ-LUECKEN-001 · Teil A — Welche echten Fragen landeten beim Menschen?
// Reine Auswertung eskalierter Chat-Fälle: nach Rolle getrennt, gebündelt (gleichartige
// Fälle = eine Zeile), mit dem echten Wortlaut daneben — damit der Owner weiß, welchen
// FAQ-Text er als nächstes schreibt. Kein Modellaufruf, nur Admin.

const ROLE_LABEL = {
  customer: 'Kunde',
  driver: 'Fahrer',
  partner: 'Partner',
  landing: 'Website (ohne Konto)',
};
const CATEGORY_LABEL = {
  harassment: 'Belästigung',
  insult: 'Beleidigung',
  threat_or_violence: 'Bedrohung oder Gewalt',
  discrimination: 'Diskriminierung',
  unsafe_ride: 'Unsichere Fahrt',
  bad_experience: 'Schlechte Erfahrung',
  fare_dispute: 'Fahrpreis beanstanden',
  lost_item: 'Fundsache',
  accessibility: 'Barrierefreiheit',
  account_or_app: 'Konto oder App',
  application_or_partnership: 'Bewerbung oder Partnerschaft',
  other: 'Sonstiges',
};
const AUSGANG_LABEL = {
  beantwortet: 'beantwortet',
  uebergeben: 'übergeben',
  fundsache: 'Fundsache',
  offen: 'offen',
};
const fmt = (value) => (value ? String(value).slice(0, 16).replace('T', ' ') : '');

export default function FaqLuecken() {
  const [data, setData] = useState(null);
  const [thin, setThin] = useState(null);
  // SUPPORT-KI-KONTO-001 (Teil C): das Lern-Protokoll der KI-Gespräche.
  const [chats, setChats] = useState(null);
  const [days, setDays] = useState(90);
  const [role, setRole] = useState('driver');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setError('');
    api.get('/admin/support/faq-luecken', { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Auswertung nicht verfügbar'));
    // SUPPORT-FAQ-LUECKEN-002 (Teil B, Stufe 1): zweite Spalte DANEBEN — Teil A bleibt.
    api.get('/admin/support/faq-luecken/duenn', { params: { days: Math.min(90, days) } })
      .then((r) => setThin(r.data))
      .catch(() => setThin(null));
    const request = api.get('/admin/assistant/chat-log', { params: { days, limit: 100 } });
    if (request && typeof request.then === 'function') {
      request.then((r) => setChats(r.data)).catch(() => setChats(null));
    } else {
      setChats(null);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const current = data?.roles?.find((r) => r.role === role);
  const currentThin = thin?.roles?.find((r) => r.role === role);
  const currentChats = (chats?.sessions || []).filter((session) => session.audience === role);

  return (
    <div className="assistant-page">
      <PageHeader
        title="FAQ-Lücken"
        description="Welche echten Fragen landeten beim Menschen? Gebündelt nach Rolle, mit Wortlaut — damit klar ist, welcher FAQ-Text als nächstes geschrieben wird."
        actions={
        <button type="button" className="assistant-reindex" onClick={load}>
          <RefreshCw size={15} /> Neu laden
        </button>
      }
      />

      <section className="assistant-boundary" aria-label="Datenlage">
        <div className="assistant-boundary-icon"><BookOpenText size={23} /></div>
        <div>
          <span>Was hier drinsteht — und was nicht</span>
          <h2>Nur eskalierte Chat-Fälle</h2>
          <p>{data?.note || 'Nur eskalierte Chat-Fälle. Nicht eskalierte Fragen und die Güte des FAQ-Treffers werden nicht erfasst.'}</p>
        </div>
        <div className="assistant-boundary-status">
          <label>
            Zeitraum{' '}
            <select aria-label="Zeitraum in Tagen" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={30}>30 Tage</option>
              <option value={90}>90 Tage</option>
              <option value={180}>180 Tage</option>
              <option value={365}>365 Tage</option>
            </select>
          </label>
          <small>{data ? `${data.total} eskalierte Fälle` : '…'}</small>
        </div>
      </section>

      {error && <div className="assistant-error" role="alert">{error}</div>}

      {data && (
        <>
          <div className="assistant-toggle-grid" role="tablist" aria-label="Rolle">
            {data.roles.map((r) => (
              <button
                type="button"
                key={r.role}
                role="tab"
                aria-selected={role === r.role}
                data-role={r.role}
                className={`assistant-toggle ${role === r.role ? 'is-on' : ''}`}
                onClick={() => setRole(r.role)}
              >
                <span><strong>{ROLE_LABEL[r.role] || r.role}</strong><small>{r.gaps.length} Lücken · {r.cases} Fälle{thin ? ` · ${(thin.roles.find((t) => t.role === r.role)?.gaps || []).length} dünn/eskaliert` : ''}</small></span>
              </button>
            ))}
          </div>

          <section className="assistant-config" aria-label={`Lücken ${ROLE_LABEL[role]}`}>
            {!current || current.gaps.length === 0 ? (
              <p className="assistant-ready">Keine eskalierten Fälle für {ROLE_LABEL[role]} im Zeitraum.</p>
            ) : (
              <div className="assistant-gap-list">
                {current.gaps.map((gap, index) => (
                  <article className="assistant-gap-row" key={`${gap.category}-${index}`} data-gap-row>
                    <span className="assistant-gap-status is-gap">{gap.count}×</span>
                    <div>
                      <strong>{CATEGORY_LABEL[gap.category] || gap.category}{gap.open ? ` · ${gap.open} offen` : ''}</strong>
                      <ul>
                        {gap.examples.map((text, i) => <li key={i}>„{text}"</li>)}
                      </ul>
                      <small>
                        zuletzt {fmt(gap.last_at)} · Fälle:{' '}
                        {gap.ticket_ids.map((id) => (
                          <a key={id} href={`/support?ticket=${encodeURIComponent(id)}`}>{String(id).slice(0, 8)}</a>
                        )).reduce((acc, el, i) => (i === 0 ? [el] : [...acc, ' · ', el]), [])}
                      </small>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="assistant-config" aria-label={`Dünn beantwortet ${ROLE_LABEL[role]}`} data-thin-column>
            <div className="assistant-section-heading">
              <div>
                <span>Teil B · beantwortet, aber die FAQ trug wenig</span>
                <h2>Dünn beantwortet &amp; eskaliert — {ROLE_LABEL[role]}</h2>
                <p>{thin?.note || 'Fragen, die die KI beantwortet hat, obwohl die FAQ wenig hergab. Kein Personenbezug; Fragekern redigiert; 90 Tage.'}{thin?.thin_rule ? ` Regel: ${thin.thin_rule}.` : ''}</p>
                {/* FAQ-LUECKEN-AUCH-ESKALATIONEN-001: Gesamtzahlen je Lücken-Art */}
                {thin?.arten ? (
                  <p><small>{thin.arten.eskaliert}× eskaliert · {thin.arten.beantwortet_duenn}× dünn beantwortet (alle Rollen, {thin.days} Tage)</small></p>
                ) : null}
              </div>
            </div>
            {!thin ? (
              <p className="assistant-ready">Zweite Spalte nicht verfügbar.</p>
            ) : !currentThin || currentThin.gaps.length === 0 ? (
              <p className="assistant-ready">Keine dünn beantworteten oder eskalierten Fragen für {ROLE_LABEL[role]} in den letzten {thin.days} Tagen.</p>
            ) : (
              <div className="assistant-gap-list">
                {currentThin.gaps.map((gap, index) => (
                  <article className="assistant-gap-row" key={`thin-${gap.category}-${index}`} data-thin-row data-gap-art={gap.art || 'beantwortet_duenn'}>
                    <span className="assistant-gap-status is-gap">{gap.count}×</span>
                    <div>
                      {/* FAQ-LUECKEN-AUCH-ESKALATIONEN-001: die Art steht sichtbar an jedem Bündel */}
                      <strong>{gap.art === 'eskaliert' ? '⤴ eskaliert · ' : ''}{CATEGORY_LABEL[gap.category] || gap.category}{gap.no_hit ? ` · ${gap.no_hit}× ohne FAQ-Treffer` : ''}</strong>
                      <ul>
                        {gap.examples.map((text, i) => <li key={i}>„{text}"</li>)}
                      </ul>
                      <small>zuletzt {fmt(gap.last_at)} · Überdeckung ⌀ {gap.coverage_avg}</small>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          {/* SUPPORT-KI-KONTO-001 (Teil C): was die KI gefragt wurde, was sie geantwortet hat,
              wie es ausging — auch ohne Fall. Reine Anzeige, ändert nichts an der KI. */}
          <section className="assistant-config" aria-label={`KI-Gespräche ${ROLE_LABEL[role]}`} data-chat-column>
            <div className="assistant-section-heading">
              <div>
                <span>Teil C · was die KI gefragt wurde und was sie geantwortet hat</span>
                <h2>KI-Gespräche — {ROLE_LABEL[role]}</h2>
                <p>{chats?.note || 'KI-Gespräche aus den Apps und von der Website — auch die ohne Fall. Text regelbasiert redigiert; Freitext kann weiterhin Personen identifizieren. Zugriff nur für berechtigte Admins.'}</p>
                {chats?.summary?.[role] ? (
                  <p><small>
                    {chats.summary[role].answered || 0}× beantwortet · {chats.summary[role].ask || 0}× Rückfrage ·{' '}
                    {(chats.summary[role].escalated || 0) + (chats.summary[role].appended || 0)}× an das Team übergeben ({chats.days} Tage)
                  </small></p>
                ) : null}
              </div>
            </div>
            {!chats ? (
              <p className="assistant-ready">KI-Gespräche nicht verfügbar.</p>
            ) : currentChats.length === 0 ? (
              <p className="assistant-ready">Noch keine KI-Gespräche für {ROLE_LABEL[role]} im Zeitraum.</p>
            ) : (
              <div className="assistant-gap-list">
                {currentChats.map((session, index) => (
                  <article className="assistant-gap-row" key={`chat-${index}`} data-chat-row data-chat-ausgang={session.ausgang}>
                    <span className={`assistant-gap-status ${session.ausgang === 'uebergeben' ? 'is-gap' : ''}`}>
                      {AUSGANG_LABEL[session.ausgang] || session.ausgang}
                    </span>
                    <div>
                      <ul>
                        {session.turns.map((turn, i) => (
                          <li key={i}><strong>{turn.role === 'user' ? 'Frage' : 'KI'}:</strong> {turn.text}</li>
                        ))}
                      </ul>
                      <small>{fmt(session.started_at)} UTC</small>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
